'use client'

import { useState, useTransition } from 'react'
import { sendMagicLink, type SignInResult } from '../_lib/actions'

// Friendly copy for redirect-arrived error codes from the callback /
// exchange routes. Tone target per docs/reference/design-system.md:
// soft, calm, informative — not alarming.
const ERROR_COPY: Record<string, string> = {
  stale_link: 'That sign-in link has expired or was already used. Enter your email below for a fresh one.',
  missing_code: "That sign-in link is missing some information. Try entering your email below to get a new one.",
  already_used: 'That sign-in link has already been used. Enter your email for a new one.',
  wrong_host: "That sign-in link wasn't issued for this site. Sign in below to start fresh.",
  bad_sig: "That sign-in link couldn't be verified. Enter your email below to get a new one.",
  session_failed: "Couldn't finish signing you in. Try again below.",
}

export default function LoginForm({
  next,
  initialError,
}: {
  next?: string
  initialError?: string
}) {
  const [pending, startTransition] = useTransition()
  const [result, setResult] = useState<SignInResult | null>(null)
  const [bannerVisible, setBannerVisible] = useState(!!initialError)

  function onSubmit(formData: FormData) {
    setResult(null)
    setBannerVisible(false)
    startTransition(async () => {
      const r = await sendMagicLink(formData)
      setResult(r)
    })
  }

  if (result?.ok) {
    return (
      <div
        role="status"
        className="rounded-[var(--radius-card,0.75rem)] border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-6"
      >
        <p className="font-semibold text-[var(--color-text-primary)]">Check your email</p>
        <p className="mt-2 text-[var(--color-text-secondary)]">
          If{' '}
          <span className="font-medium text-[var(--color-text-primary)]">{result.email}</span>{' '}
          has an order with us, or a product being transferred to you, a sign-in link is on its
          way. Use the email you ordered with. The link expires in one hour. No email after a few
          minutes? Contact the store.
        </p>
      </div>
    )
  }

  const bannerCopy = initialError ? ERROR_COPY[initialError] ?? null : null

  return (
    <form action={onSubmit} className="flex flex-col gap-4">
      {bannerVisible && bannerCopy && (
        <div
          role="status"
          className="rounded-[var(--radius-sm,0.375rem)] border border-[var(--color-warning-border)] bg-[var(--color-warning-surface)] px-4 py-3 text-sm text-[var(--color-warning-strong)]"
        >
          {bannerCopy}
        </div>
      )}
      {next && <input type="hidden" name="next" value={next} />}
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-[var(--color-text-primary)]">
          Email address <span aria-hidden="true">*</span>
        </span>
        <input
          name="email"
          type="email"
          required
          autoComplete="email"
          autoFocus
          aria-required="true"
          disabled={pending}
          className="rounded-[var(--radius-sm,0.375rem)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-4 py-3 text-[var(--color-text-primary)] focus:border-[var(--color-cta-edge)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)]"
          placeholder="you@example.com"
        />
      </label>

      {result && !result.ok && (
        <p role="alert" className="text-sm text-[var(--color-error-strong)]">
          {result.error}
        </p>
      )}

      <button
        type="submit"
        disabled={pending}
        className="cta-primary self-start rounded-[var(--radius-sm,0.375rem)] px-6 py-3 font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      >
        {pending ? 'Sending link…' : 'Send sign-in link'}
      </button>
    </form>
  )
}
